import React from "react";
import { Box } from "@mantine/core";
import { tokens } from "@blurb/codex-react";
import { GraphQL } from "@rpi/openapi-api";
import { useParams } from "react-router-dom";
import { useAuthUser } from "../../../hooks/useAuthUser";
import { EventLogEntry } from "./eventLogs/EventLogEntry";
import type { OrderEvent } from "./eventLogs/types";
import { OrderTabContent } from "./OrderTabContent";

const getEventKey = (event: OrderEvent, index: number): string | number => `${event.eventTime}-${index}`;

export const OrderEventLogsTab: React.FC = () => {
  const { customerPublicId, customerOrderId } = useParams();
  const user = useAuthUser();
  const isSuperAdmin = user.is.SUPER_ADMIN;
  const hasOrderIdentifier = !!customerPublicId && !!customerOrderId;

  const orderIdentifier = {
    customerPublicId: customerPublicId ?? "",
    customerOrderId: customerOrderId ?? "",
  };

  const { data, isLoading, error } = GraphQL.useOrderEventsQuery(
    { orderIdentifier, isSandbox: user.isSandbox, isSuperAdmin },
    { enabled: hasOrderIdentifier }
  );

  const { data: callbacksData } = GraphQL.useOrderCallbacksQuery(
    { orderIdentifier, isSandbox: user.isSandbox },
    { enabled: isSuperAdmin && hasOrderIdentifier }
  );

  const events = data?.orderByIdentifier?.orderEvents ?? [];
  const callbacks = callbacksData?.orderByIdentifier?.callbacks ?? [];

  return (
    <OrderTabContent
      data={events}
      isLoading={isLoading}
      error={error}
      emptyMessage="No events found for this order."
      loadedTestId="order-event-logs-loaded"
    >
      {(events) => (
        <Box sx={{ padding: tokens.spacing[4] }}>
          {events.map((event, index) => (
            <EventLogEntry
              key={getEventKey(event, index)}
              event={event}
              callbacks={callbacks}
              isLastEntry={index === events.length - 1}
              isSuperAdmin={isSuperAdmin}
            />
          ))}
        </Box>
      )}
    </OrderTabContent>
  );
};
